import { useState } from 'react';

import { useSession } from '../../app/useSession';
import { useTeamMember } from '../../app/useTeamMember';
import { useToast } from '../../components/Toast';
import { BotSubNav } from './BotSubNav';
import type { BotScoringRuleRow } from './bot.schema';
import { useBotScoringRules, useUpdateScoringRule } from './botQueries';
import styles from './bot.module.css';

function describeCondition(c: Record<string, unknown>): string {
  if ('exists' in c) {
    return `יש ערך ל-${String(c.exists)}`;
  }
  if ('match' in c && typeof c.match === 'object' && c.match !== null) {
    const entries = Object.entries(c.match as Record<string, unknown>);
    return entries.map(([k, v]) => `${k} = ${String(v)}`).join(' · ');
  }
  return JSON.stringify(c);
}

function RuleRow({ rule, orgId }: { rule: BotScoringRuleRow; orgId: string | null }) {
  const update = useUpdateScoringRule(orgId);
  const toast = useToast();
  const [weight, setWeight] = useState(String(rule.weight));

  async function saveWeight() {
    const n = Number(weight);
    if (!Number.isInteger(n)) {
      toast.error('המשקל חייב להיות מספר שלם');
      return;
    }
    try {
      await update.mutateAsync({ id: rule.id, patch: { weight: n } });
      toast.success('המשקל עודכן');
    } catch {
      toast.error('לא הצלחנו לעדכן · נסה שוב');
    }
  }

  async function toggleActive() {
    try {
      await update.mutateAsync({ id: rule.id, patch: { is_active: !rule.is_active } });
    } catch {
      toast.error('לא הצלחנו לעדכן · נסה שוב');
    }
  }

  return (
    <div className={styles.tableRow}>
      <span>
        <strong>{rule.label_he}</strong>
        {rule.description_he ? <div className={styles.rowMeta}>{rule.description_he}</div> : null}
      </span>
      <span className={styles.rowMeta}>{describeCondition(rule.condition_json)}</span>
      <input
        type="number"
        className={styles.weightInput}
        value={weight}
        onChange={(e) => { setWeight(e.target.value); }}
        onBlur={() => { void saveWeight(); }}
      />
      <button
        type="button"
        className={rule.is_active ? styles.btnPrimary : styles.btnSecondary}
        onClick={() => { void toggleActive(); }}
      >
        {rule.is_active ? 'פעיל' : 'כבוי'}
      </button>
      <span className={styles.rowMeta}>
        <code dir="ltr" style={{ fontSize: 11 }}>{rule.rule_key}</code>
      </span>
    </div>
  );
}

export function ScoringRulesPage() {
  const session = useSession();
  const sessionObj = session.status === 'signed-in' ? session.session : null;
  const { data: teamMember } = useTeamMember(sessionObj);
  const orgId = teamMember?.organization_id ?? null;

  const { data: rules = [], isLoading } = useBotScoringRules(orgId);

  const totalWeight = rules.filter((r) => r.is_active).reduce((sum, r) => sum + r.weight, 0);

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <h1 className={styles.title}>כללי דירוג ליד</h1>
          <p className={styles.subtitle}>
            כל כלל מוסיף או מוריד נקודות מציון הליד (0-100). סכום המשקולות הפעילות:{' '}
            <span className="num">{String(totalWeight)}</span>
          </p>
        </div>
      </header>

      <BotSubNav />

      {isLoading ? (
        <p className={styles.notice}>טוען...</p>
      ) : rules.length === 0 ? (
        <p className={styles.empty}>טרם הוגדרו כללי דירוג.</p>
      ) : (
        <div className={styles.list}>
          <div className={styles.tableHead}>
            <span>שם</span>
            <span>תנאי</span>
            <span>משקל</span>
            <span>סטטוס</span>
            <span>מפתח</span>
          </div>
          {rules.map((r) => (
            <RuleRow key={r.id} rule={r} orgId={orgId} />
          ))}
        </div>
      )}
    </div>
  );
}
